import type { ComponentProps } from "@chatbotgang/etude/emotion-react/ComponentProps";
import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import { getRouteApi, Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { defineStyles } from "@/internal/emotion";
import { useWhatsappCompliance } from "@/pages/channels/hooks/useWhatsappCompliance";
import { useWhatsappConversationalComponents } from "@/pages/channels/hooks/useWhatsappConversationalComponents";
import { ChannelQueriesContextProvider } from "@/pages/channels/pages/detail/channelQueriesContext";
import { Automations } from "@/pages/channels/pages/detail/whatsapp/Automations";
import { Compliance } from "@/pages/channels/pages/detail/whatsapp/Compliance";
import { General } from "@/pages/channels/pages/detail/whatsapp/General";
import { getVisibleWhatsappTabs } from "@/pages/channels/pages/detail/whatsapp/utils/getVisibleWhatsappTabs";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/$type/$id");
const TabsSchema = z.enum(["general", "compliance", "automations"]);
type TabsSchemaType = z.infer<typeof TabsSchema>;

// Module-level JSX, not built inline per-render: each entry is a `<Trans>` or
// a page component, so re-rendering the map wouldn't save anything, and
// keeping `i18nKey` as a literal at its own call site (rather than passed
// through a variable) is what keeps it visible to i18n-ally's static
// scanning and any unused-key sweep. `<Trans>` calls `useTranslation()`
// internally, so the label still re-renders on a language change.
const TAB_LABELS: Record<TabsSchemaType, React.ReactNode> = {
  general: <Trans i18nKey="page.channel.tabs.general" />,
  compliance: <Trans i18nKey="page.channel.tabs.compliance" />,
  automations: <Trans i18nKey="page.channel.tabs.automations" />,
};

// No Suspense boundary around Automations: it renders its own loading and
// error states from a non-suspense query, so its Meta-backed GET failing
// stays inside the tab panel instead of unwinding to the router's
// `errorComponent`. General/Compliance don't suspend either.
const TAB_CONTENT: Record<TabsSchemaType, React.ReactNode> = {
  general: <General />,
  compliance: <Compliance />,
  automations: <Automations />,
};

interface TabPanelProps extends ComponentProps<"div"> {
  index: TabsSchemaType;
  value: TabsSchemaType;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
};

const styles = defineStyles({
  link: css({
    textDecoration: "none",
  }),
  pageTitle: css({
    marginBottom: 0,
  }),
});

// Exported (only) so the tab-gating test can render it directly, without
// going through `ChannelQueriesContextProvider` -- this component doesn't
// touch `ChannelQueriesContext` itself, only its General/Compliance/
// Automations children do.
export const WhatsappDetailPageInternal: React.FC = () => {
  const { t } = useTranslation();
  const { id } = route.useParams();
  const enableWhatsappCompliance = useWhatsappCompliance();
  const enableWhatsappAutomations = useWhatsappConversationalComponents();
  const visibleTabs = getVisibleWhatsappTabs({
    compliance: enableWhatsappCompliance,
    automations: enableWhatsappAutomations,
  });
  // "general" is always in `visibleTabs`, so more than one entry means at
  // least one optional tab is on -- render the tab bar. Both off collapses
  // back to the pre-tabs layout (bare `<General />`, no tab chrome).
  const showTabs = visibleTabs.length > 1;
  const [value, setValue] = useState<TabsSchemaType>(TabsSchema.Enum.general);
  // A feature gate can flip off while the user is sitting on that gate's
  // tab -- `value` then names a tab that is no longer rendered, which MUI's
  // <Tabs> rejects (invalid-value warning) and every panel goes blank. Fall
  // back to the always-present General tab instead of persisting the stale
  // selection.
  const activeTab = visibleTabs.includes(value)
    ? value
    : TabsSchema.Enum.general;
  const handleChange = useHandler<ComponentProps<typeof Tabs>["onChange"]>(
    (_, newValue) => {
      const result = TabsSchema.safeParse(newValue);
      if (!result.success) return;
      setValue(result.data);
    },
  );

  return (
    <Stack gap="8px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Link to="/channels" css={styles.link}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("channels.title")}
          </Typography>
        </Link>
        <Link
          to="/channels/$type/$id"
          params={{
            type: "whatsapp",
            id,
          }}
          css={styles.link}
        >
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t("common.whatsapp")}
          </Typography>
        </Link>
      </Breadcrumbs>
      <PageTitle css={styles.pageTitle}>
        <Trans i18nKey="page.channel.whatsapp.detail.title" />
      </PageTitle>
      <InfoBox
        variant="plain"
        description={t("page.channel.whatsapp.detail.optIn.infoBox")}
        href={t("page.channel.whatsapp.detail.optIn.infoBox.link")}
        sx={{ width: "430px" }}
      />
      {showTabs ? (
        <>
          <Tabs value={activeTab} onChange={handleChange}>
            {visibleTabs.map((tabKey) => (
              <Tab key={tabKey} label={TAB_LABELS[tabKey]} value={tabKey} />
            ))}
          </Tabs>
          {visibleTabs.map((tabKey) => (
            <TabPanel key={tabKey} value={activeTab} index={tabKey}>
              {TAB_CONTENT[tabKey]}
            </TabPanel>
          ))}
        </>
      ) : (
        <General />
      )}
    </Stack>
  );
};

export const WhatsappDetailPage: React.FC = () => {
  return (
    <Suspense fallback={<BarLoading />}>
      <ChannelQueriesContextProvider>
        <WhatsappDetailPageInternal />
      </ChannelQueriesContextProvider>
    </Suspense>
  );
};
